import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { resultTypes, tipTypes } from '../../../appSettings';
import HotelCard from '../hotelCard/hotelCard';
import ArticleCard from '../articleCard/articleCard';
import TipCard from '../tipCard/tipCard';
import GroupTripCard from '../groupTripCard/groupTripCard';
import SkeletonLoader from '../skeletonLoader/skeletonLoader';
import NoContent from '../noContent/noContent';
import {
  getExcursionUrl,
  getSubTitle,
  getRelativeImageUrlFromStatic,
} from '../../../utils/helpers';
import {
  logProductImpressionMixedEvent,
  getHotelProduct,
  getDestinationProduct,
  getExcursionProduct,
  getGroupTripProduct,
  getArticleProduct,
} from '../../../utils/tracking';
import { useEffect } from 'react';
import { serialize } from '../../../../server/utils/serialize';
import { addSerializedComponent } from '../../../redux/actions/resultsActions';

const resultListMix = [
  {
    type: resultTypes.HOTELS,
    numberOfItems: 2,
  },
  {
    type: resultTypes.DESTINATIONS,
    numberOfItems: 1,
  },
  {
    type: resultTypes.EXCURSIONS,
    numberOfItems: 1,
  },
  {
    type: resultTypes.GROUP_TRIPS,
    numberOfItems: 1,
    shouldRender: (iteration) => iteration > 0,
  },
  {
    type: resultTypes.ARTICLES,
    numberOfItems: 1,
    shouldRender: (iteration) => iteration > 0,
  },
];

const ResultListAll = ({
  loadingAll,
  mixListCount,
  hotels,
  destinations,
  excursions,
  grouptrips,
  articles,
  siteSettings,
  labels,
  onAddSerializedComponent,
  getSerializedComponents,
}) => {
  const renderHotels = (mixItemHotel, mixIteration) => {
    const startingIndex = mixIteration * mixItemHotel.numberOfItems;
    const hotelsSlice = hotels?.slice(
      startingIndex,
      startingIndex + mixItemHotel.numberOfItems,
    );
    if (!hotelsSlice) {
      return null;
    }

    const { pax } = siteSettings.filters;

    return hotelsSlice.map((hotel) => {
      const card = (
        <HotelCard
          objectId={hotel.objectID}
          key={hotel.name}
          siteId={siteSettings.siteId}
          imageDomain={siteSettings.imageUrl}
          image={hotel.imageRelativeUrl}
          name={hotel.name}
          url={hotel.url}
          location={hotel.location}
          rating={hotel.doubledRating}
          guestRating={hotel.guestRating}
          tripadvisorRating={hotel.tripAdvisorRating}
          description={hotel.hotelDescription}
          itemClassifications={hotel.classifications}
          numberOfAdults={pax.selectedNumberOfAdults}
          numberOfChildren={pax.selectedNumberOfChildren}
          salesPrice={hotel.price}
          originalPrice={hotel.originalPrice}
          hasDiscount={hotel.hasDiscount}
          departureDate={hotel.departureDate}
          departureCode={hotel.departureCode}
          departureAirports={siteSettings.departureAirports}
          labels={labels}
          trackingProduct={getHotelProduct(hotel)}
          filters={siteSettings.filters}
          classifications={siteSettings.classifications}
        />
      );

      if (getSerializedComponents) {
        onAddSerializedComponent(serialize(card));
      }

      return card;
    });
  };

  const renderDestinations = (mixItemDestination, mixIteration) => {
    const startingIndex = mixIteration * mixItemDestination.numberOfItems;
    const destinationsSlice = destinations?.slice(
      startingIndex,
      startingIndex + mixItemDestination.numberOfItems,
    );
    if (!destinationsSlice) {
      return null;
    }

    return destinationsSlice.map((destination) => {
      return (
        <TipCard
          key={destination.name}
          objectId={destination.objectID}
          imageRelativeUrl={destination.imageRelativeUrl}
          imageDomain={siteSettings.imageUrl}
          type={tipTypes.DESTINATION}
          itemClassifications={destination.classifications}
          title={destination.name}
          subTitle={destination.areaCountry}
          text={destination.introduction}
          url={destination.url}
          labels={labels}
          trackingProduct={getDestinationProduct(destination)}
        />
      );
    });
  };

  const renderExcursions = (mixItemExcursion, mixIteration) => {
    // Each new loop we continue with the next hotels
    const startingIndex = mixIteration * mixItemExcursion.numberOfItems;
    const excursionsSlice = excursions?.slice(
      startingIndex,
      startingIndex + mixItemExcursion.numberOfItems,
    );
    if (!excursionsSlice) {
      return null;
    }

    return excursionsSlice.map((excursion) => {
      return (
        <TipCard
          key={excursion.heading}
          objectId={excursion.objectID}
          imageRelativeUrl={excursion.imageRelativeUrl}
          imageDomain={siteSettings.imageUrl}
          type={tipTypes.EXCURSION}
          itemClassifications={excursion.classifications}
          title={excursion.heading}
          subTitle={getSubTitle(excursion)}
          text={excursion.shortDescription}
          url={getExcursionUrl(siteSettings.siteId, excursion.objectID)}
          labels={labels}
          trackingProduct={getExcursionProduct(excursion)}
        />
      );
    });
  };

  const renderGroupTrips = (mixItemGrouptrip, mixIteration) => {
    if (
      mixItemGrouptrip.shouldRender &&
      mixItemGrouptrip.shouldRender(mixIteration)
    ) {
      // -1 to adjust the index to not render when index === 0
      const startingIndex = mixIteration * mixItemGrouptrip.numberOfItems - 1;
      const grouptripsSlice = grouptrips?.slice(
        startingIndex,
        startingIndex + mixItemGrouptrip.numberOfItems,
      );
      if (!grouptripsSlice) {
        return null;
      }
      return grouptripsSlice.map((groupTrip) => {
        const subTitle = `${groupTrip.resort}, ${groupTrip.area}`;
        return (
          <GroupTripCard
            key={groupTrip.title}
            objectId={groupTrip.objectID}
            siteId={siteSettings.siteId}
            imageRelativeUrl={getRelativeImageUrlFromStatic(groupTrip.image)}
            imageDomain={siteSettings.imageUrl}
            title={groupTrip.title}
            subTitle={subTitle}
            itemClassifications={groupTrip.classifications}
            text={groupTrip.metaDescription}
            url={groupTrip.url}
            tripInfo={{
              hotelImage: groupTrip.hotelInfo.imageRelativeUrl,
              hotelTitle: groupTrip.hotelInfo.name,
              hotelRating: groupTrip.hotelInfo.doubledRating,
              mealPackageAvailable: true,
              departureDate: groupTrip.hotelInfo.departureDate,
              departureAirports: groupTrip.hotelInfo.departureAirports,
              price: groupTrip.hotelInfo.price,
            }}
            labels={labels}
            trackingProduct={getGroupTripProduct(groupTrip)}
          />
        );
      });
    }
    return null;
  };

  const renderArticles = (mixItemArticle, mixIteration) => {
    if (
      mixItemArticle.shouldRender &&
      mixItemArticle.shouldRender(mixIteration)
    ) {
      // -1 to adjust the index to not render when index === 0
      const startingIndex = mixIteration * mixItemArticle.numberOfItems - 1;
      const articlesSlice = articles?.slice(
        startingIndex,
        startingIndex + mixItemArticle.numberOfItems,
      );
      if (!articlesSlice) {
        return null;
      }

      return articlesSlice.map((article) => {
        return (
          <ArticleCard
            key={article.title}
            objectId={article.objectID}
            imageDomain={siteSettings.imageUrl}
            imageRelativeUrl={getRelativeImageUrlFromStatic(article.image)}
            header={article.title}
            url={article.url}
            itemClassifications={article.classifications}
            description={article.metaDescription}
            labels={labels}
            trackingProduct={getArticleProduct(article)}
          />
        );
      });
    }
    return null;
  };

  const noContentCheck = () => {
    return (
      hotels?.length === 0 &&
      destinations?.length === 0 &&
      excursions?.length === 0 &&
      grouptrips?.length === 0 &&
      articles?.length === 0
    );
  };

  if (loadingAll) {
    return <SkeletonLoader />;
  }

  if (noContentCheck()) {
    return <NoContent type={resultTypes.ALL} />;
  }

  useEffect(() => {
    logProductImpressionMixedEvent(
      hotels,
      destinations,
      excursions,
      grouptrips,
      articles,
      resultListMix,
      mixListCount,
    );
  }, []);

  return (
    <section className="vf-result-container">
      <div className="vf-result-container__2x1">
        {[...Array(mixListCount)].map((dummyCountItem, currentMixCount) => {
          return resultListMix.map((resultListItem) => {
            switch (resultListItem.type) {
              case resultTypes.HOTELS:
                return renderHotels(resultListItem, currentMixCount);
              case resultTypes.DESTINATIONS:
                return renderDestinations(resultListItem, currentMixCount);
              case resultTypes.EXCURSIONS:
                return renderExcursions(resultListItem, currentMixCount);
              case resultTypes.GROUP_TRIPS:
                return renderGroupTrips(resultListItem, currentMixCount);
              case resultTypes.ARTICLES:
                return renderArticles(resultListItem, currentMixCount);
              default:
                return null;
            }
          });
        })}
      </div>
    </section>
  );
};

ResultListAll.defaultProps = {
  articles: undefined,
};

ResultListAll.propTypes = {
  loadingAll: PropTypes.bool.isRequired,
  mixListCount: PropTypes.number.isRequired,
  hotels: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  destinations: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  excursions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  grouptrips: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  articles: PropTypes.arrayOf(PropTypes.shape()),
  siteSettings: PropTypes.shape({
    siteId: PropTypes.number.isRequired,
    imageUrl: PropTypes.string.isRequired,
    filters: PropTypes.shape({
      selectedPrimaryClassifications: PropTypes.arrayOf(PropTypes.string),
      selectedSecondaryClassifications: PropTypes.arrayOf(PropTypes.string),
      pax: PropTypes.shape({
        selectedNumberOfAdults: PropTypes.number,
        selectedNumberOfChildren: PropTypes.number,
      }).isRequired,
    }).isRequired,
    classifications: PropTypes.shape({}).isRequired,
    departureAirports: PropTypes.arrayOf(PropTypes.shape().isRequired)
      .isRequired,
  }).isRequired,
  labels: PropTypes.shape({}).isRequired,
};

const mapDispatchToProps = (dispatch) => {
  return {
    onAddSerializedComponent: (serialized) => {
      dispatch(addSerializedComponent(serialized));
    },
  };
};

const mapStateToProps = ({ siteSettings, attributeCollection, results }) => {
  const labels = {
    tipLabel: attributeCollection.tipLabel,
    tipTypeDestinationName: attributeCollection.tipTypeDestinationName,
    tipTypeExcursionName: attributeCollection.tipTypeExcursionName,
    tipTypeGroupTripName: attributeCollection.tipTypeGroupTripName,
    buttonText: attributeCollection.buttonText,
    hotelLabel: attributeCollection.hotelLabel,
    fromLabel: attributeCollection.fromLabel,
    selectHotel: attributeCollection.selectHotel,
    hotelNoPriceText: attributeCollection.hotelNoPriceText,
    hotelIncludedText: attributeCollection.hotelIncludedText,
    hotelAdults: attributeCollection.hotelAdults,
    hotelAdult: attributeCollection.hotelAdult,
    hotelChildren: attributeCollection.hotelChildren,
    hotelChild: attributeCollection.hotelChild,
    hotelFrom: attributeCollection.hotelFrom,
    hotelAnd: attributeCollection.hotelAnd,
    hotelDeparture: attributeCollection.hotelDeparture,
    fallbackGroupTripImage: attributeCollection.fallbackGroupTripImage,
    fallbackArticleImage: attributeCollection.fallbackArticleImage,
    groupTripFlightAndHotelText:
      attributeCollection.groupTripFlightAndHotelText,
    groupTripFromText: attributeCollection.groupTripFromText,
    groupTripOnePersonText: attributeCollection.groupTripOnePersonText,
    groupTripOnePersonFromText: attributeCollection.groupTripOnePersonFromText,
  };
  return {
    labels,
    siteSettings,
    loadingAll: results.loadingAll,
    getSerializedComponents: results.getSerializedComponents,
    onAddSerializedComponent: results.onAddSerializedComponent,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResultListAll);
